import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface SidebarState {
  isOpen: boolean;
  isCollapsed: boolean;
  isPinned: boolean;
  expandedItems: string[];
  searchTerm: string;
  activeItem: string | null;
}

interface SidebarActions {
  toggleOpen: () => void;
  setOpen: (open: boolean) => void;
  toggleCollapse: () => void;
  setCollapsed: (collapsed: boolean) => void;
  togglePin: () => void;
  toggleExpandedItem: (itemPath: string) => void;
  setExpandedItems: (items: string[]) => void;
  setSearchTerm: (term: string) => void;
  setActiveItem: (itemPath: string | null) => void;
  reset: () => void;
}

export const useSidebarStore = create<SidebarState & SidebarActions>()(
  persist(
    (set, get) => ({
      // Initial state
      isOpen: true,
      isCollapsed: false,
      isPinned: false,
      expandedItems: [],
      searchTerm: '',
      activeItem: null,

      // Actions
      toggleOpen: () => {
        set((state) => ({ isOpen: !state.isOpen }));
      },

      setOpen: (open) => {
        set({ isOpen: open });
      },

      toggleCollapse: () => {
        set((state) => ({ isCollapsed: !state.isCollapsed }));
      },

      setCollapsed: (collapsed) => {
        set({ isCollapsed: collapsed });
      },

      togglePin: () => {
        set((state) => {
          const newPinned = !state.isPinned;
          // When pinning, ensure sidebar is expanded
          if (newPinned && state.isCollapsed) {
            return { isPinned: newPinned, isCollapsed: false };
          }
          return { isPinned: newPinned };
        });
      },

      toggleExpandedItem: (itemPath) => {
        set((state) => {
          const isExpanded = state.expandedItems.includes(itemPath);
          if (isExpanded) {
            return {
              expandedItems: state.expandedItems.filter(item => item !== itemPath)
            };
          } else {
            return {
              expandedItems: [...state.expandedItems, itemPath]
            };
          }
        });
      },

      setExpandedItems: (items) => {
        set({ expandedItems: items });
      },

      setSearchTerm: (term) => {
        set({ searchTerm: term });
      },

      setActiveItem: (itemPath) => {
        set({ activeItem: itemPath });
      },

      reset: () => {
        set({
          isOpen: true,
          isCollapsed: false,
          isPinned: false,
          expandedItems: [],
          searchTerm: '',
          activeItem: null
        });
      }
    }),
    {
      name: 'sidebar-storage',
      partialize: (state) => ({
        isCollapsed: state.isCollapsed,
        isPinned: state.isPinned,
        expandedItems: state.expandedItems
      })
    }
  )
);

